/* ==========================================================================
   BRYVO: planner.js
   The Website Planner: a short guided questionnaire that suggests a project type
   and shows its starting price. It is a guide, not a quotation calculator.

   - Questions and options are rendered by sections/planner.liquid.
   - Each possible suggestion (name, price, explanation) is a block in the theme editor.
   - The rules that map answers to a suggestion are in decide() below.
   - Answers are kept in this browser tab only (sessionStorage) so they survive moving
     between steps and can be handed to the project enquiry form. Nothing is sent anywhere.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__bryvoPlanner) return;
  window.__bryvoPlanner = true;

  var doc = document;
  var KEY = 'bryvo:planner';
  var reduce = doc.documentElement.getAttribute('data-motion') === 'off';

  /* A step is only asked when its condition is true. */
  var CONDITIONS = {
    keep: function (a) { return a.need === 'redesign'; },
    content: function (a) { return a.need !== 'redesign'; }
  };

  /* ---------- The recommendation rules ---------- */
  function has(list, value) {
    return Array.isArray(list) && list.indexOf(value) > -1;
  }

  function decide(a) {
    if (!a.need || a.need === 'unsure') return 'unsure';

    if (a.need === 'redesign') {
      var size = a.size || 'unsure';
      var keep = a.keep || 'unsure';
      if (size === 'large') return keep === 'little' ? 're_bespoke' : 're_larger';
      if (size === 'full') {
        if (keep === 'most') return 're_basic';
        return keep === 'little' ? 're_larger' : 're_standard';
      }
      if (size === 'single') return keep === 'little' ? 're_basic' : 're_refresh';
      /* a handful of pages, or not sure */
      if (keep === 'most') return 're_refresh';
      return keep === 'little' ? 're_standard' : 're_basic';
    }

    /* New website */
    if (a.kind === 'shop' || a.goal === 'sell' || has(a.features, 'sell')) return 'new_shop';
    var levels = ['new_onepage', 'new_small', 'new_professional', 'new_larger'];
    var index = { single: 0, few: 1, full: 2, large: 3, unsure: 1 }[a.size || 'unsure'];
    var extras = ['booking', 'blog', 'migration'].filter(function (f) { return has(a.features, f); }).length;
    if (extras >= 2 && index < 3) index += 1;
    return levels[index];
  }

  /* ---------- Storage ---------- */
  function store() {
    try {
      var s = window.sessionStorage;
      s.getItem(KEY);
      return s;
    } catch (error) {
      return null;
    }
  }

  function readState() {
    var s = store();
    if (!s) return null;
    try {
      return JSON.parse(s.getItem(KEY) || 'null');
    } catch (error) {
      return null;
    }
  }

  function writeState(state) {
    var s = store();
    if (!s) return;
    try {
      s.setItem(KEY, JSON.stringify(state));
    } catch (error) {
      /* the planner still works without storage */
    }
  }

  function clearState() {
    var s = store();
    if (!s) return;
    try {
      s.removeItem(KEY);
    } catch (error) {
      /* nothing to do */
    }
  }

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function focusElement(el) {
    if (!el) return;
    try {
      el.focus({ preventScroll: true });
    } catch (error) {
      el.focus();
    }
  }

  /* ---------- One planner ---------- */
  function init(root) {
    if (root.getAttribute('data-ready')) return;
    root.setAttribute('data-ready', 'true');

    var form = root.querySelector('[data-planner-form]');
    var top = root.querySelector('[data-planner-top]');
    var count = root.querySelector('[data-planner-count]');
    var bar = root.querySelector('[data-planner-bar]');
    var progress = root.querySelector('[data-planner-progress]');
    var nav = root.querySelector('[data-planner-nav]');
    var back = root.querySelector('[data-planner-back]');
    var next = root.querySelector('[data-planner-next]');
    var nextLabel = root.querySelector('[data-planner-next-label]');
    var errorNote = root.querySelector('[data-planner-error]');
    var result = root.querySelector('[data-planner-result]');
    var care = root.querySelector('[data-planner-care]');
    var recap = root.querySelector('[data-planner-answers]');
    if (!form || !result || !next || !back) return;

    var steps = Array.prototype.slice.call(form.querySelectorAll('[data-step]'));
    var template = root.getAttribute('data-count-template') || 'Question [current] of [total]';
    var answers = {};
    var index = 0;
    var pointerPick = false;
    var advanceTimer = 0;

    root.classList.add('is-stepped');
    if (top) top.hidden = false;
    if (nav) nav.hidden = false;

    function activeSteps() {
      return steps.filter(function (step) {
        var condition = CONDITIONS[step.getAttribute('data-step')];
        return !condition || condition(answers);
      });
    }

    function readStep(step) {
      var key = step.getAttribute('data-step');
      if (step.hasAttribute('data-multi')) {
        var values = [];
        each(step.querySelectorAll('input:checked'), function (input) { values.push(input.value); });
        answers[key] = values;
      } else {
        var chosen = step.querySelector('input:checked');
        if (chosen) answers[key] = chosen.value;
        else delete answers[key];
      }
    }

    function labelsFor(step) {
      var labels = [];
      each(step.querySelectorAll('input:checked'), function (input) {
        var label = input.parentNode.querySelector('.option__label');
        if (label) labels.push(label.textContent.trim());
      });
      return labels;
    }

    function setProgress(fraction, text) {
      if (bar) bar.style.setProperty('--progress', fraction.toFixed(3));
      if (progress) {
        progress.setAttribute('aria-valuenow', String(Math.round(fraction * 100)));
        if (text) progress.setAttribute('aria-valuetext', text);
      }
    }

    function saveProgress(done, resultInfo, list) {
      writeState({
        v: 1,
        done: !!done,
        need: answers.need || '',
        raw: answers,
        result: resultInfo || null,
        answers: list || []
      });
    }

    function show(i, moveFocus, direction) {
      var list = activeSteps();
      index = Math.max(0, Math.min(list.length - 1, i));
      var step = list[index];
      steps.forEach(function (s) {
        s.hidden = s !== step;
        s.classList.remove('is-entering');
      });
      result.hidden = true;
      form.hidden = false;
      if (count) count.hidden = false;
      if (errorNote) errorNote.hidden = true;
      if (!reduce) {
        step.style.setProperty('--dir', direction < 0 ? '-1.5rem' : '1.5rem');
        void step.offsetWidth;
        step.classList.add('is-entering');
      }
      var text = template.replace('[current]', String(index + 1)).replace('[total]', String(list.length));
      if (count) count.textContent = text;
      setProgress(index / list.length, text);
      back.hidden = index === 0;
      if (nextLabel) nextLabel.textContent = next.getAttribute(index === list.length - 1 ? 'data-label-finish' : 'data-label-next') || nextLabel.textContent;
      if (moveFocus) focusElement(step.querySelector('legend'));
    }

    function showOutcome(key) {
      var chosen = null;
      var fallback = null;
      var first = null;
      each(root.querySelectorAll('[data-result]'), function (outcome) {
        var name = outcome.getAttribute('data-result');
        outcome.hidden = true;
        if (!first) first = outcome;
        if (name === key && !chosen) chosen = outcome;
        if (name === 'unsure' && !fallback) fallback = outcome;
      });
      chosen = chosen || fallback || first;
      if (chosen) chosen.hidden = false;
      return chosen;
    }

    function finish(moveFocus) {
      var list = activeSteps();
      var activeKeys = list.map(function (step) { return step.getAttribute('data-step'); });
      Object.keys(answers).forEach(function (key) {
        if (activeKeys.indexOf(key) === -1) delete answers[key];
      });
      list.forEach(readStep);

      var key = decide(answers);
      var outcome = showOutcome(key);
      var recapList = [];
      if (recap) recap.textContent = '';
      list.forEach(function (step) {
        var labels = labelsFor(step);
        if (!labels.length) return;
        var question = step.getAttribute('data-short') || step.getAttribute('data-step');
        recapList.push({ q: question, a: labels.join(', ') });
        if (recap) {
          var group = doc.createElement('div');
          var dt = doc.createElement('dt');
          var dd = doc.createElement('dd');
          dt.textContent = question;
          dd.textContent = labels.join(', ');
          group.appendChild(dt);
          group.appendChild(dd);
          recap.appendChild(group);
        }
      });
      if (care) care.hidden = !(answers.care === 'yes' || answers.care === 'maybe');

      var titleNode = outcome ? outcome.querySelector('[data-result-title]') : null;
      var priceNode = outcome ? outcome.querySelector('[data-result-price]') : null;
      saveProgress(true, {
        key: key,
        title: titleNode ? titleNode.textContent.trim() : '',
        price: priceNode ? priceNode.textContent.replace(/\s+/g, ' ').trim() : ''
      }, recapList);

      form.hidden = true;
      if (count) count.hidden = true;
      setProgress(1, '');
      result.hidden = false;
      if (!reduce) {
        result.classList.remove('is-entering');
        void result.offsetWidth;
        result.classList.add('is-entering');
      }
      if (moveFocus) {
        focusElement(result);
        var rect = root.getBoundingClientRect();
        if (rect.top < 90) window.scrollTo({ top: Math.max(0, window.pageYOffset + rect.top - 150), behavior: reduce ? 'auto' : 'smooth' });
      }
    }

    function advance() {
      var list = activeSteps();
      var step = list[index];
      if (!step) return;
      readStep(step);
      var key = step.getAttribute('data-step');
      if (!step.hasAttribute('data-multi') && !answers[key]) {
        if (errorNote) errorNote.hidden = false;
        focusElement(step.querySelector('input'));
        return;
      }
      saveProgress(false);
      if (index >= activeSteps().length - 1) finish(true);
      else show(index + 1, true, 1);
    }

    function reset() {
      window.clearTimeout(advanceTimer);
      each(form.querySelectorAll('input'), function (input) { input.checked = false; });
      answers = {};
      clearState();
      show(0, true, -1);
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      window.clearTimeout(advanceTimer);
      advance();
    });

    back.addEventListener('click', function () {
      window.clearTimeout(advanceTimer);
      show(index - 1, true, -1);
    });

    /* Picking a single-choice answer with a mouse or finger moves straight on.
       Keyboard users move through options with the arrow keys, so they use the Next button. */
    form.addEventListener('pointerdown', function (event) {
      pointerPick = !!(event.target.closest && event.target.closest('.option'));
    });
    form.addEventListener('keydown', function () {
      pointerPick = false;
    });

    form.addEventListener('change', function (event) {
      var input = event.target;
      var step = input.closest ? input.closest('[data-step]') : null;
      if (!step) return;
      if (step.hasAttribute('data-multi') && input.checked) {
        /* "None of these" and the other choices are mutually exclusive. */
        each(step.querySelectorAll('input'), function (other) {
          if (other === input) return;
          if (input.value === 'none' || other.value === 'none') other.checked = false;
        });
      }
      readStep(step);
      if (errorNote) errorNote.hidden = true;
      saveProgress(false);
      if (pointerPick && input.type === 'radio') {
        pointerPick = false;
        window.clearTimeout(advanceTimer);
        advanceTimer = window.setTimeout(advance, 280);
      }
    });

    each(root.querySelectorAll('[data-planner-reset]'), function (button) {
      button.addEventListener('click', reset);
    });

    var edit = root.querySelector('[data-planner-edit]');
    if (edit) edit.addEventListener('click', function () { show(0, true, -1); });

    /* Restore answers given earlier in this tab. */
    var saved = readState();
    if (saved && saved.raw) {
      answers = saved.raw;
      steps.forEach(function (step) {
        var value = answers[step.getAttribute('data-step')];
        each(step.querySelectorAll('input'), function (input) {
          input.checked = Array.isArray(value) ? value.indexOf(input.value) > -1 : value === input.value;
        });
      });
    }
    if (saved && saved.done) finish(false);
    else show(0, false, 1);

    /* Theme editor: selecting a "Suggestion" block previews it. */
    root.addEventListener('shopify:block:select', function (event) {
      var outcome = event.target.closest ? event.target.closest('[data-result]') : null;
      if (!outcome) return;
      showOutcome(outcome.getAttribute('data-result'));
      form.hidden = true;
      result.hidden = false;
    });
    root.addEventListener('shopify:block:deselect', function () {
      show(0, false, 1);
    });
  }

  function initAll(scope) {
    each((scope || doc).querySelectorAll('[data-planner]'), function (root) {
      try {
        init(root);
      } catch (error) {
        if (window.console && console.warn) console.warn('[BRYVO] planner skipped: ' + (error && error.message));
      }
    });
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', function () { initAll(doc); });
  else initAll(doc);

  doc.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
